// Plan — web home for the mobile Travel + Discover task categories. Create a
// trip or a "discover nearby" plan with category-specific metadata, then let
// the backend AI build a rich itinerary of subtasks (flights, hotels, activities
// …) which you can book and regenerate. Decoupled from the legacy dashboard so
// it themes cleanly in light + dark.
//
// Plans are date-bucketed (same as tasks); the date selector loads plans for a
// given day. New plans are created on the selected date.

import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Plane, Compass, X, MapPin, CalendarDays } from 'lucide-react';
import { createPlanTask, fetchPlans } from '../services/breakdownService';
import { getCategoryColor } from '../constants/categories';
import PlanDetail from './PlanDetail';
import './PlanScreen.css';

const DISCOVER_PRESETS = ['coffee', 'restaurant', 'gym', 'bar', 'bakery'];
const TIME_WINDOWS = ['today', 'this week', 'this month'];

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyTravel = () => ({
  destination: '', origin: '', start_date: '', end_date: '',
  traveler_count: 1, budget: '', preferences_text: '',
});
const emptyDiscover = () => ({ what: '', radius_mi: 2, time_window: 'this week', deals_only: false });

function planSummary(t) {
  const m = t.category_metadata || {};
  if ((t.category || '').toLowerCase() === 'discover') {
    return [m.what, m.radius_mi ? `${m.radius_mi} mi` : null, m.time_window, m.deals_only ? 'deals only' : null]
      .filter(Boolean).join(' · ');
  }
  return [m.destination ? `to ${m.destination}` : null, m.origin ? `from ${m.origin}` : null,
    m.start_date && m.end_date ? `${m.start_date} → ${m.end_date}` : m.start_date || null,
    m.traveler_count ? `${m.traveler_count} traveler${m.traveler_count > 1 ? 's' : ''}` : null]
    .filter(Boolean).join(' · ');
}

function PlanScreen() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateISO, setDateISO] = useState(todayISO);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setPlans(await fetchPlans(dateISO)); } catch (_) { /* handled */ } finally { setLoading(false); }
  }, [dateISO]);

  useEffect(() => { load(); }, [load]);

  const onCreated = (task) => {
    setCreateOpen(false);
    setPlans((prev) => [task, ...prev]);
    setDetail(task);
  };

  // keep the open detail in sync when its subtasks/metadata change
  const onDetailChanged = (updated) => {
    setPlans((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    setDetail(updated);
  };

  return (
    <div className="plan-screen">
      <div className="plan-inner">
        <header className="plan-head">
          <div>
            <p className="plan-sub">AI-built trips &amp; nearby discoveries</p>
          </div>
          <button className="plan-new-btn" onClick={() => setCreateOpen(true)}>
            <Plus size={18} strokeWidth={2.5} /> New plan
          </button>
        </header>

        <label className="plan-date">
          <CalendarDays size={15} />
          <span>Showing plans for</span>
          <input type="date" value={dateISO} onChange={(e) => setDateISO(e.target.value)} />
        </label>

        {loading ? (
          <div className="plan-empty">Loading plans…</div>
        ) : plans.length === 0 ? (
          <div className="plan-empty">
            <Compass size={28} />
            <p>No trips or discoveries for this date. Create one to let the AI build a plan.</p>
          </div>
        ) : (
          <ul className="plan-list">
            {plans.map((t) => {
              const isDiscover = (t.category || '').toLowerCase() === 'discover';
              const color = getCategoryColor(isDiscover ? 'Other' : 'Travel');
              const count = (t.subtasks || []).length;
              return (
                <li key={t.id}>
                  <button className="plan-card" onClick={() => setDetail(t)}>
                    <span className="plan-card-icon" style={{ color, background: 'var(--tudu-glass-2)' }}>
                      {isDiscover ? <Compass size={20} /> : <Plane size={20} />}
                    </span>
                    <span className="plan-card-body">
                      <span className="plan-card-top">
                        <span className="plan-card-title">{t.task_text}</span>
                        <span className="plan-chip" style={{ color, borderColor: color }}>
                          {isDiscover ? 'Discover' : 'Travel'}
                        </span>
                      </span>
                      <span className="plan-card-meta">{planSummary(t) || 'Tap to build the plan'}</span>
                    </span>
                    <span className="plan-card-count">
                      {count > 0 ? `${count} step${count > 1 ? 's' : ''}` : 'Build'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {createOpen && (
        <CreatePlanModal dateISO={dateISO} onClose={() => setCreateOpen(false)} onCreated={onCreated} />
      )}
      {detail && (
        <PlanDetail task={detail} onClose={() => setDetail(null)} onChanged={onDetailChanged} />
      )}
    </div>
  );
}

function CreatePlanModal({ dateISO, onClose, onCreated }) {
  const [type, setType] = useState('Travel');
  const [travel, setTravel] = useState(emptyTravel);
  const [discover, setDiscover] = useState(emptyDiscover);
  const [saving, setSaving] = useState(false);

  const isTravel = type === 'Travel';
  const valid = isTravel ? travel.destination.trim() : discover.what.trim();

  const create = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      const metadata = isTravel
        ? { ...travel, traveler_count: Number(travel.traveler_count) || 1 }
        : { ...discover, radius_mi: Number(discover.radius_mi) || 2 };
      const title = isTravel ? `Trip to ${travel.destination.trim()}` : `Discover ${discover.what.trim()}`;
      const task = await createPlanTask({ type, title, metadata, dateISO });
      onCreated(task);
    } catch (_) {
      window.alert('Could not create the plan. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="plan-modal-overlay" onClick={onClose}>
      <div className="plan-modal" onClick={(e) => e.stopPropagation()}>
        <div className="plan-modal-head">
          <h2>New plan</h2>
          <button className="plan-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        <div className="plan-modal-body">
          <div className="plan-type-toggle">
            <button className={isTravel ? 'active' : ''} onClick={() => setType('Travel')}>
              <Plane size={16} /> Travel
            </button>
            <button className={!isTravel ? 'active' : ''} onClick={() => setType('Discover')}>
              <Compass size={16} /> Discover
            </button>
          </div>

          {isTravel ? (
            <TravelFields value={travel} onChange={setTravel} />
          ) : (
            <DiscoverFields value={discover} onChange={setDiscover} />
          )}
        </div>

        <div className="plan-modal-foot">
          <button className="plan-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="plan-btn-primary" disabled={saving || !valid} onClick={create}>
            {saving ? 'Creating…' : 'Create plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TravelFields({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="plan-fields">
      <Field label="Destination" required>
        <input className="plan-input" value={value.destination}
          placeholder="Miami, FL (required to build the plan)"
          onChange={(e) => set({ destination: e.target.value })} />
      </Field>
      <Field label="Origin">
        <input className="plan-input" value={value.origin} placeholder="Dallas, TX"
          onChange={(e) => set({ origin: e.target.value })} />
      </Field>
      <div className="plan-field-row">
        <Field label="Start date">
          <input type="date" className="plan-input" value={value.start_date}
            onChange={(e) => set({ start_date: e.target.value })} />
        </Field>
        <Field label="End date">
          <input type="date" className="plan-input" value={value.end_date} min={value.start_date || undefined}
            onChange={(e) => set({ end_date: e.target.value })} />
        </Field>
      </div>
      <div className="plan-field-row">
        <Field label="Travelers">
          <input type="number" min="1" className="plan-input" value={value.traveler_count}
            onChange={(e) => set({ traveler_count: e.target.value })} />
        </Field>
        <Field label="Budget">
          <input className="plan-input" value={value.budget} placeholder="$1500"
            onChange={(e) => set({ budget: e.target.value })} />
        </Field>
      </div>
      <Field label="Tell the AI about this trip">
        <textarea className="plan-input plan-textarea" rows={3} value={value.preferences_text}
          placeholder="Beach + good food, relaxed pace, 1 museum…"
          onChange={(e) => set({ preferences_text: e.target.value })} />
      </Field>
    </div>
  );
}

function DiscoverFields({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="plan-fields">
      <Field label="What are you looking for?" required>
        <input className="plan-input" value={value.what} placeholder="coffee, gym, bakery…"
          onChange={(e) => set({ what: e.target.value })} />
        <div className="plan-chips">
          {DISCOVER_PRESETS.map((p) => (
            <button key={p} type="button"
              className={`plan-chip-btn${value.what === p ? ' active' : ''}`}
              onClick={() => set({ what: p })}>{p}</button>
          ))}
        </div>
      </Field>
      <div className="plan-field-row">
        <Field label="Radius (miles)">
          <input type="number" min="0.5" step="0.5" className="plan-input" value={value.radius_mi}
            onChange={(e) => set({ radius_mi: e.target.value })} />
        </Field>
        <Field label="When?">
          <div className="plan-chips">
            {TIME_WINDOWS.map((w) => (
              <button key={w} type="button"
                className={`plan-chip-btn${value.time_window === w ? ' active' : ''}`}
                onClick={() => set({ time_window: w })}>{w}</button>
            ))}
          </div>
        </Field>
      </div>
      <label className="plan-toggle-row">
        <span><MapPin size={14} /> Only places with active deals</span>
        <span className={`plan-switch${value.deals_only ? ' on' : ''}`} onClick={() => set({ deals_only: !value.deals_only })}>
          <span className="plan-switch-knob" />
        </span>
      </label>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="plan-field">
      <span className="plan-field-label">{label}{required && <span className="plan-req"> *</span>}</span>
      {children}
    </label>
  );
}

export default PlanScreen;
